import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, ArrowRight, CalendarHeart, Baby, Stethoscope, Activity } from "lucide-react";
import { SITE } from "../lib/site";

const CTAS = [
  {
    icon: CalendarHeart,
    label: "Agenda tu consulta",
    msg: "Hola Dr. Jairo Franco, quisiera agendar una consulta. ¿Qué horarios tiene disponibles?",
    testid: "cta-consulta",
  },
  {
    icon: Activity,
    label: "Agenda tu ultrasonido",
    msg: "Hola Dr. Jairo Franco, me gustaría agendar un ultrasonido obstétrico. ¿Qué horarios tiene disponibles?",
    testid: "cta-ultrasonido",
  },
  {
    icon: Stethoscope,
    label: "Reserva tu valoración ginecológica",
    msg: "Hola Dr. Jairo Franco, me gustaría reservar una valoración ginecológica. ¿Qué horarios tiene disponibles?",
    testid: "cta-valoracion",
  },
  {
    icon: Baby,
    label: "Agenda tu control prenatal",
    msg: "Hola Dr. Jairo Franco, quisiera iniciar mi control prenatal. ¿Qué horarios tiene disponibles?",
    testid: "cta-prenatal",
  },
];

export default function WhatsAppCTABlock({ variant = "default", highlight }) {
  const isDark = variant === "dark";
  return (
    <section
      data-testid="whatsapp-cta-block"
      className={`relative ${isDark ? "py-20 md:py-28" : "py-16 md:py-24"} overflow-hidden`}
    >
      <div
        className={`absolute inset-0 -z-10 ${
          isDark
            ? "bg-gradient-to-br from-brand-deep via-brand-medical to-brand-bright"
            : "bg-gradient-to-b from-white via-brand-snow to-brand-cloud/50"
        }`}
      />
      {isDark && (
        <>
          <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-brand-bright/20 blur-3xl pointer-events-none" />
        </>
      )}

      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-7">
            <p className={`text-xs uppercase tracking-[0.28em] mb-3 ${isDark ? "text-brand-silver" : "text-brand-medical"}`}>
              <MessageCircle className="inline w-3.5 h-3.5 mr-1.5" strokeWidth={1.6} />
              Atención directa por WhatsApp
            </p>
            <h2 className={`font-serif text-4xl md:text-5xl leading-[1.05] ${isDark ? "text-white" : "text-brand-deep"}`}>
              Da el siguiente paso en tu <em className={`not-italic ${isDark ? "text-brand-silver" : "text-gradient-blue"}`}>cuidado</em>.
            </h2>
          </div>
          <div className={`lg:col-span-5 self-end font-light leading-relaxed text-lg ${
            isDark ? "text-white/80" : "text-brand-deep/70"
          }`}>
            Elige el motivo de tu visita y te respondo personalmente por WhatsApp para
            confirmar tu cita en el horario que mejor te acomode.
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {CTAS.map((c, i) => {
            const isHighlight = highlight && c.testid === highlight;
            return (
              <motion.a
                key={c.testid}
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={c.testid}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                className={`group relative flex items-center gap-4 rounded-2xl p-5 md:p-6 overflow-hidden transition-all duration-500 hairline ${
                  isHighlight
                    ? "bg-gradient-to-br from-emerald-500 to-emerald-600 text-white premium-shadow"
                    : isDark
                      ? "bg-white/10 backdrop-blur-md text-white hover:bg-white/15 ring-1 ring-white/15"
                      : "bg-white text-brand-deep premium-shadow hover:premium-shadow-hover"
                }`}
              >
                <span
                  aria-hidden
                  className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out`}
                />

                <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                  isHighlight
                    ? "bg-white/20 ring-1 ring-white/30"
                    : isDark
                      ? "bg-white/15 ring-1 ring-white/20"
                      : "bg-brand-cloud ring-1 ring-brand-silver/70"
                }`}>
                  <c.icon
                    className={`w-5 h-5 ${
                      isHighlight ? "text-white" : isDark ? "text-white" : "text-brand-medical"
                    }`}
                    strokeWidth={1.4}
                  />
                </div>

                <div className="relative flex-1 min-w-0">
                  <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] mb-1 ${
                    isHighlight ? "text-white/80" : isDark ? "text-brand-silver" : "text-brand-medical"
                  }`}>
                    <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-500 text-white text-[8px] font-bold">✓</span>
                    WhatsApp directo
                  </span>
                  <p className={`font-serif text-lg md:text-xl leading-tight ${
                    isHighlight ? "text-white" : isDark ? "text-white" : "text-brand-deep"
                  }`}>
                    {c.label}
                  </p>
                </div>

                <ArrowRight
                  className={`relative w-4 h-4 flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1 ${
                    isHighlight ? "text-white" : isDark ? "text-brand-silver" : "text-brand-medical"
                  }`}
                  strokeWidth={1.6}
                />
              </motion.a>
            );
          })}
        </div>

        <p className={`mt-6 text-center text-xs ${isDark ? "text-white/60" : "text-brand-deep/50"}`}>
          Te atiende personalmente el Dr. Jairo Franco · +52 55 7046 6065
        </p>
      </div>
    </section>
  );
}
